import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { ComponentType } from '@angular/cdk/portal';

// Import existing components we'll enhance
import { LoadingToastComponent, LoadingToastData } from '@shared/components/loading-toast/loading-toast.component';

// Configuration interfaces
export interface ToastOptions {
  duration?: number;
  action?: string;
  panelClass?: string[];
  position?: 'top-right' | 'bottom-right' | 'top-center' | 'bottom-center';
  thickness?: 'thin' | 'normal' | 'thick';
}

export interface ToastActionOptions {
  /** Auto-dismiss delay in ms (defaults to the type's standard duration). */
  duration?: number;
  /** Semantic tone driving the panel class (defaults to 'info'). */
  type?: 'success' | 'error' | 'warning' | 'info';
  position?: 'top-right' | 'bottom-right' | 'top-center' | 'bottom-center';
  thickness?: 'thin' | 'normal' | 'thick';
}

/** Handle returned by `toast.action` so callers can dismiss the toast early. */
export interface ToastActionRef {
  dismiss: () => void;
}

export interface LoadingToastOptions {
  loadingMessage?: string;
  successMessage?: string;
  errorMessage?: string;
  duration?: {
    loading?: number;
    success?: number;
    error?: number;
  };
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  thickness?: 'thin' | 'normal' | 'thick';
}

export interface BasicConfirmConfig {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'accent' | 'warn';
  icon?: string;
  iconColor?: 'primary' | 'accent' | 'warn' | 'info' | 'success' | 'warning' | 'error';
  width?: string;
}

export interface DeleteConfirmConfig extends BasicConfirmConfig {
  itemName?: string;
  itemType?: 'property' | 'expense' | 'contract' | 'loan' | 'tenant' | 'document' | 'variable' | 'building' | 'repair' | 'maintenance' | 'serviceProvider';
  warningDetails?: string[];
  cascadeInfo?: string;
  /** Exige de taper `confirmationText` avant d'activer le bouton (suppressions définitives). */
  requireConfirmation?: boolean;
  /** Texte à taper ; par défaut « DELETE » côté dialogue — passer le nom de l'élément. */
  confirmationText?: string;
}

export interface EnhancedConfirmConfig extends BasicConfirmConfig {
  showInput?: boolean;
  inputLabel?: string;
  inputType?: 'text' | 'number' | 'email' | 'password';
  inputValue?: string | number | null;
  inputMin?: number;
  inputMax?: number;
  dateInput?: boolean;
  dateLabel?: string;
  dateValue?: Date;
  textareaInput?: boolean;
  textareaLabel?: string;
  textareaValue?: string;
}

export interface LoadingOptions {
  message?: string;
  progress?: number;
  showProgress?: boolean;
  overlay?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export interface UIFeedbackConfig {
  toast: {
    position: 'top-right' | 'bottom-right' | 'top-center' | 'bottom-center';
    duration: {
      success: number;
      error: number;
      warning: number;
      info: number;
    };
    maxVisible: number;
    thickness: 'thin' | 'normal' | 'thick';
  };
  loading: {
    minDisplayTime: number;
    spinnerSize: 'small' | 'medium' | 'large';
    showProgress: boolean;
  };
  dialogs: {
    defaultWidth: string;
    closeOnBackdrop: boolean;
    animations: boolean;
  };
}

export interface LoadingOperation {
  id: string;
  message: string;
  snackBarRef: MatSnackBarRef<LoadingToastComponent>;
  complete: (result: 'success' | 'error', customMessage?: string) => void;
  cancel: () => void;
}

export interface CustomDialogConfig {
  width?: string;
  data?: unknown;
  dialogOptions?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * @domain Shared/Services
 * 
 * Centralized UI Feedback Service for consistent notifications, confirmations, and loading states.
 * 
 * This service unifies all user feedback patterns across the application:
 * - Toast notifications (success, error, warning, info, loading)
 * - Confirmation dialogs (basic, delete, enhanced with inputs)
 * - Loading indicators (toast-based, overlay, progress)
 * 
 * Features:
 * - Consistent visual design and behavior
 * - Configurable positioning, duration, and styling
 * - Type-safe interfaces for all feedback types
 * - Automatic cleanup and state management
 * - Enhanced visibility options (thickness, prominence)
 * 
 * Usage:
 * ```typescript
 * constructor(private uiFeedback: UIFeedbackService) {}
 * 
 * // Toast notifications
 * this.uiFeedback.toast.success('Operation completed!');
 * this.uiFeedback.toast.error('Something went wrong');
 * 
 * // Loading operations
 * const operation = this.uiFeedback.toast.loading('save-operation', {
 *   loadingMessage: 'Saving changes...',
 *   successMessage: 'Changes saved successfully!'
 * });
 * 
 * try {
 *   await this.saveData();
 *   operation.complete('success');
 * } catch (error) {
 *   operation.complete('error');
 * }
 * 
 * // Confirmations
 * const confirmed = await this.uiFeedback.confirm.delete({
 *   title: 'Delete Property',
 *   itemName: property.name,
 *   itemType: 'property'
 * });
 * 
 * if (confirmed) {
 *   // Proceed with deletion
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UIFeedbackService {
  private activeOperations = new Map<string, LoadingOperation>();
  private activeLoadings = new Map<string, BehaviorSubject<LoadingOptions>>();
  
  private defaultConfig: UIFeedbackConfig = {
    toast: {
      position: 'bottom-center',
      duration: {
        success: 4000,
        error: 6000,
        warning: 5000,
        info: 4000
      },
      maxVisible: 5,
      thickness: 'thick' // Enhanced visibility
    },
    loading: {
      minDisplayTime: 500,
      spinnerSize: 'medium',
      showProgress: false
    },
    dialogs: {
      defaultWidth: '400px',
      closeOnBackdrop: false,
      animations: true
    }
  };

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  /**
   * Toast notification methods
   */
  toast = {
    success: (message: string, options?: ToastOptions) => {
      this.showToast(message, 'success', options);
    },

    error: (message: string, options?: ToastOptions) => {
      this.showToast(message, 'error', options);
    },

    warning: (message: string, options?: ToastOptions) => {
      this.showToast(message, 'warning', options);
    },

    info: (message: string, options?: ToastOptions) => {
      this.showToast(message, 'info', options);
    },

    loading: (operationId: string, options?: LoadingToastOptions): LoadingOperation => {
      return this.showLoadingToast(operationId, options);
    },

    /**
     * A toast that carries a single action button (undo / retry / reload …).
     * Centralises the `MatSnackBar.open(msg, action).onAction()` pattern so
     * every actionable toast shares the app's styling and positioning.
     */
    action: (
      message: string,
      actionLabel: string,
      onAction: () => void,
      options?: ToastActionOptions
    ): ToastActionRef => {
      return this.showActionToast(message, actionLabel, onAction, options);
    }
  };

  /**
   * Confirmation dialog methods
   */
  confirm = {
    basic: (config: BasicConfirmConfig): Promise<boolean> => {
      return this.showBasicConfirmation(config);
    },

    delete: (config: DeleteConfirmConfig): Promise<boolean> => {
      return this.showDeleteConfirmation(config);
    },

    enhanced: (config: EnhancedConfirmConfig): Promise<unknown> => {
      return this.showEnhancedConfirmation(config);
    },

    custom: <T, R = unknown>(component: ComponentType<T>, config: CustomDialogConfig): Promise<R> => {
      return this.showCustomDialog<T, R>(component, config);
    }
  };

  /**
   * Loading indicator methods
   */
  loading = {
    show: (id: string, options?: LoadingOptions) => {
      this.showLoading(id, options);
    },

    hide: (id: string) => {
      this.hideLoading(id);
    },

    setProgress: (id: string, progress: number) => {
      this.setLoadingProgress(id, progress);
    },

    global: {
      show: (message?: string) => {
        this.showGlobalLoading(message);
      },

      hide: () => {
        this.hideGlobalLoading();
      }
    }
  };

  /**
   * Private implementation methods
   */
  private showToast(message: string, type: 'success' | 'error' | 'warning' | 'info', options?: ToastOptions): void {
    const config = { ...this.defaultConfig.toast, ...options };
    const duration = options?.duration || (typeof config.duration === 'object' ? config.duration[type] : config.duration);
    
    const panelClasses = [
      `${type}-notification`,
      'custom-notification',
      ...(options?.panelClass || [])
    ];

    // Add thickness class for enhanced visibility
    const thickness = options?.thickness || config.thickness;
    if (thickness === 'thick') {
      panelClasses.push('thick-notification');
    }

    const position = this.getSnackBarPosition(options?.position || config.position);

    this.snackBar.open(message, options?.action || this.translate.instant('shared.actions.close'), {
      duration,
      panelClass: panelClasses,
      horizontalPosition: position.horizontal,
      verticalPosition: position.vertical
    });
  }

  private showActionToast(
    message: string,
    actionLabel: string,
    onAction: () => void,
    options?: ToastActionOptions
  ): ToastActionRef {
    const type = options?.type || 'info';
    const config = this.defaultConfig.toast;
    const duration = options?.duration ?? config.duration[type];

    const panelClasses = [`${type}-notification`, 'custom-notification'];
    const thickness = options?.thickness || config.thickness;
    if (thickness === 'thick') {
      panelClasses.push('thick-notification');
    }

    const position = this.getSnackBarPosition(options?.position || config.position);
    const ref = this.snackBar.open(message, actionLabel, {
      duration,
      panelClass: panelClasses,
      horizontalPosition: position.horizontal,
      verticalPosition: position.vertical
    });

    ref.onAction().subscribe(() => onAction());

    return { dismiss: () => ref.dismiss() };
  }

  private showLoadingToast(operationId: string, options?: LoadingToastOptions): LoadingOperation {
    const defaults: Required<LoadingToastOptions> = {
      loadingMessage: this.translate.instant('shared.uiFeedback.processing'),
      successMessage: this.translate.instant('shared.uiFeedback.operationSuccess'),
      errorMessage: this.translate.instant('shared.uiFeedback.operationFailed'),
      duration: {
        loading: 0, // Indefinite by default
        success: 4000,
        error: 6000
      },
      showSuccessToast: true,
      showErrorToast: true,
      thickness: this.defaultConfig.toast.thickness
    };

    const config = { ...defaults, ...options };
    config.duration = { ...defaults.duration, ...options?.duration };

    // Dismiss any existing operation with the same ID
    this.dismissLoadingToast(operationId);

    const panelClasses = ['loading-notification', 'custom-notification'];
    if (config.thickness === 'thick') {
      panelClasses.push('thick-notification');
    }

    // Show loading toast with spinner
    const position = this.getSnackBarPosition(this.defaultConfig.toast.position);
    const snackBarRef = this.snackBar.openFromComponent(LoadingToastComponent, {
      data: {
        message: config.loadingMessage,
        type: 'loading',
        thickness: config.thickness,
        prominence: config.thickness === 'thick' ? 'prominent' : 'normal'
      } as LoadingToastData,
      duration: config.duration.loading || undefined,
      panelClass: panelClasses,
      horizontalPosition: position.horizontal,
      verticalPosition: position.vertical
    });

    const operation: LoadingOperation = {
      id: operationId,
      message: config.loadingMessage,
      snackBarRef,
      complete: (result: 'success' | 'error', customMessage?: string) => {
        this.completeLoadingOperation(operationId, result, customMessage, config);
      },
      cancel: () => {
        this.dismissLoadingToast(operationId);
      }
    };

    this.activeOperations.set(operationId, operation);
    return operation;
  }

  private completeLoadingOperation(
    operationId: string, 
    result: 'success' | 'error', 
    customMessage?: string,
    config?: Required<LoadingToastOptions>
  ): void {
    const operation = this.activeOperations.get(operationId);
    if (!operation || !config) return;

    // Dismiss the loading toast
    operation.snackBarRef.dismiss();
    this.activeOperations.delete(operationId);

    // Show completion toast if enabled
    const shouldShowToast = result === 'success' ? config.showSuccessToast : config.showErrorToast;
    if (shouldShowToast) {
      const message = customMessage || (result === 'success' ? config.successMessage : config.errorMessage);
      const duration = config.duration[result];
      
      this.showToast(message, result, { duration, thickness: config.thickness });
    }
  }

  private dismissLoadingToast(operationId: string): void {
    const operation = this.activeOperations.get(operationId);
    if (operation) {
      operation.snackBarRef.dismiss();
      this.activeOperations.delete(operationId);
    }
  }

  private async showBasicConfirmation(config: BasicConfirmConfig): Promise<boolean> {
    const { UnifiedConfirmationDialogComponent } = await import('@shared/components/unified-dialogs/unified-confirmation-dialog.component');
    
    const dialogRef = this.dialog.open(UnifiedConfirmationDialogComponent, {
      width: config.width || this.defaultConfig.dialogs.defaultWidth,
      disableClose: !this.defaultConfig.dialogs.closeOnBackdrop,
      data: {
        ...config,
        dangerousAction: config.confirmColor === 'warn'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

  private async showDeleteConfirmation(config: DeleteConfirmConfig): Promise<boolean> {
    const { UnifiedDeleteDialogComponent } = await import('@shared/components/unified-dialogs/unified-delete-dialog.component');
    
    const dialogRef = this.dialog.open(UnifiedDeleteDialogComponent, {
      width: config.width || '500px',
      disableClose: true, // Delete confirmations should not be accidentally closed
      data: config
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

  private async showEnhancedConfirmation(config: EnhancedConfirmConfig): Promise<unknown> {
    // We'll implement this with the existing EnhancedConfirmationDialogComponent for now
    // and can create a unified version later if needed
    const { EnhancedConfirmationDialogComponent } = await import('@shared/components/enhanced-confirmation-dialog/enhanced-confirmation-dialog.component');
    
    const dialogRef = this.dialog.open(EnhancedConfirmationDialogComponent, {
      width: config.width || this.defaultConfig.dialogs.defaultWidth,
      disableClose: !this.defaultConfig.dialogs.closeOnBackdrop,
      data: config
    });

    return dialogRef.afterClosed().toPromise();
  }

  private async showCustomDialog<T, R = unknown>(component: ComponentType<T>, config: CustomDialogConfig): Promise<R> {
    const dialogRef = this.dialog.open(component, {
      width: config.width || this.defaultConfig.dialogs.defaultWidth,
      disableClose: !this.defaultConfig.dialogs.closeOnBackdrop,
      data: config.data || config,
      ...config.dialogOptions
    });

    return dialogRef.afterClosed().toPromise();
  }

  private showLoading(id: string, options?: LoadingOptions): void {
    const loadingSubject = new BehaviorSubject<LoadingOptions>(options || {});
    this.activeLoadings.set(id, loadingSubject);
  }

  private hideLoading(id: string): void {
    const loadingSubject = this.activeLoadings.get(id);
    if (loadingSubject) {
      loadingSubject.complete();
      this.activeLoadings.delete(id);
    }
  }

  private setLoadingProgress(id: string, progress: number): void {
    const loadingSubject = this.activeLoadings.get(id);
    if (loadingSubject) {
      const currentValue = loadingSubject.value;
      loadingSubject.next({ ...currentValue, progress });
    }
  }

  private showGlobalLoading(_message?: string): void {
    // TODO: Implement global loading overlay
  }

  private hideGlobalLoading(): void {
    // TODO: Implement global loading overlay hide
  }

  private getSnackBarPosition(position: string) {
    switch (position) {
      case 'top-right':
        return { horizontal: 'end' as const, vertical: 'top' as const };
      case 'top-center':
        return { horizontal: 'center' as const, vertical: 'top' as const };
      case 'bottom-center':
        return { horizontal: 'center' as const, vertical: 'bottom' as const };
      case 'bottom-right':
      default:
        return { horizontal: 'end' as const, vertical: 'bottom' as const };
    }
  }

  /**
   * Configuration methods
   */
  updateConfig(config: Partial<UIFeedbackConfig>): void {
    this.defaultConfig = { ...this.defaultConfig, ...config };
  }

  getConfig(): UIFeedbackConfig {
    return { ...this.defaultConfig };
  }
}
